import { useState, useEffect, useMemo } from 'react';
import { extractErrorMessage } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';
import type { FormacionPrecio, Params } from './types';
import { simularPrecios, TIPOS_PUBLICACION, type ItemSimulacion } from './simular';
import { fmt, fmtCosto, fmtPorcentaje } from './formato';
import { NumInput, SemaforoRentabilidad } from './ui';
import { AYUDA_MAYORISTAS, PreciosMayoristas } from './Mayoristas';

const redondear2 = (n: number) => Math.round(n * 100) / 100;

// ─── Calculadora individual ────────────────────────────────────────────────
// La cuenta la hace el backend con la misma fórmula y los parámetros guardados que «Por publicación».
export function Calculadora({ p }: { p: Params }) {
  const [costoSinIva, setCostoSinIva] = useState(0);
  const [flete, setFlete]             = useState(0);
  const [pesoKg, setPesoKg]           = useState(0.5);
  const [precioActual, setPrecioActual] = useState(0);
  const [calc, setCalc] = useState<FormacionPrecio | null>(null);
  const [error, setError] = useState<string | null>(null);

  const item = useMemo((): ItemSimulacion | null => (costoSinIva > 0 ? {
    costoConIva: redondear2(costoSinIva * (1 + p.ivaAlicuota / 100) + flete),
    pesoKg: pesoKg > 0 ? pesoKg : null,
    tipo: p.tipo,
    rentabilidadPct: p.rentabilidadPct,
    precioActual: precioActual > 0 ? precioActual : null,
  } : null), [costoSinIva, flete, pesoKg, precioActual, p]);
  const itemDebounced = useDebounce(item, 300);

  useEffect(() => {
    if (!itemDebounced) { setCalc(null); setError(null); return; }
    let cancelado = false;
    setError(null);
    simularPrecios([itemDebounced])
      .then(([r]) => { if (!cancelado) setCalc(r ?? null); })
      .catch(e => { if (!cancelado) { setCalc(null); setError(extractErrorMessage(e)); } });
    return () => { cancelado = true; };
  }, [itemDebounced]);

  const precio = calc?.precioSugeridoRedondeado ?? null;
  const parte = (pct: number) => (precio != null ? `−${fmtCosto((precio * pct) / 100)}` : '—');
  const tipo = TIPOS_PUBLICACION.find(t => t.id === p.tipo)?.label ?? p.tipo;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Producto</h2>
          <div className="grid grid-cols-2 gap-4">
            <NumInput label="Costo proveedor (sin IVA)" value={costoSinIva} onChange={setCostoSinIva} step={100} />
            <NumInput label="Flete / otros costos" value={flete} onChange={setFlete} step={100} hint="Se suma al costo con IVA." />
          </div>
          <NumInput label="Peso del producto" value={pesoKg} onChange={setPesoKg} prefix="" suffix="kg" step="any" min={0}
            hint="Elige la tarifa de envío de la solapa Parámetros." />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Verificación inversa</h2>
          <NumInput label="Precio actual en ML" value={precioActual} onChange={setPrecioActual} step={100}
            hint="¿Cuánto te queda limpio con tu precio de hoy?" />
          {calc?.rentabilidadNetaActual && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
              <SemaforoRentabilidad netaPct={calc.rentabilidadNetaActual.pctSobreCosto} objetivoPct={p.rentabilidadPct} />
              Con tu precio de hoy te quedan {fmtCosto(calc.rentabilidadNetaActual.monto)} limpios.
            </p>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden sticky top-4">
          <div className="bg-brand-50 border-b border-brand-100 px-4 py-4">
            <p className="text-xs font-semibold text-brand-600 uppercase tracking-wide">Precio sugerido</p>
            <p className="text-3xl font-bold text-brand-700 font-mono mt-0.5">{fmt(precio)}</p>
            {calc && <p className="mt-1 text-[11px] text-brand-600">{tipo}, redondeado para arriba a múltiplos de ${calc.redondeoPrecio}</p>}
            {calc?.rentabilidadNeta && (
              <div className="mt-2"><SemaforoRentabilidad netaPct={calc.rentabilidadNeta.pctSobreCosto} objetivoPct={p.rentabilidadPct} /></div>
            )}
          </div>
          {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}
          {calc && precio != null ? (
            <div className="divide-y divide-slate-100">
              {[
                { label: 'Costo con IVA', val: fmtCosto(calc.costoBaseConIva) },
                { label: `+ Rentabilidad ${fmtPorcentaje(calc.rentabilidadPct)} del costo`, val: fmtCosto(calc.gananciaEstimada) },
                { label: `+ Envío${calc.envio.rango ? ` (${calc.envio.rango})` : ''}`, val: fmtCosto(calc.envio.costo) },
                ...(calc.comision.cargoFijo > 0 ? [{ label: '+ Cargo fijo ML', val: fmtCosto(calc.comision.cargoFijo) }] : []),
                { label: `Comisión ML (${fmtPorcentaje(calc.comision.pct)})`, val: parte(calc.comision.pct), red: true },
                { label: `Costo fijo (${fmtPorcentaje(calc.costoFijoPct)})`, val: parte(calc.costoFijoPct), red: true },
                { label: 'IIBB SIRTAC y Tucumán', val: parte(calc.iibbSirtacPct + calc.iibbTucumanPct), red: true },
                { label: `IIBB provincial (${fmtPorcentaje(calc.iibbProvincialPct)})`, val: parte(calc.iibbProvincialPct), red: true },
                { label: 'Precio sin IIBB provincial', val: fmtCosto(calc.precioSinIibb) },
                { label: 'Rentabilidad bruta', val: `${fmtCosto(calc.rentabilidadBruta?.monto)} (${fmtPorcentaje(calc.rentabilidadBruta?.pctSobreCosto)})` },
              ].map(({ label, val, red }) => (
                <div key={label} className="flex justify-between items-center gap-3 px-4 py-2.5">
                  <span className="text-sm text-slate-500">{label}</span>
                  <span className={`text-sm font-mono whitespace-nowrap ${red ? 'text-red-500' : 'text-slate-700'}`}>{val}</span>
                </div>
              ))}
              <div className="flex justify-between items-center gap-3 px-4 py-3 bg-brand-50">
                <span className="text-sm font-semibold text-brand-700">Rentabilidad neta</span>
                <span className="text-sm font-mono font-semibold text-brand-700 whitespace-nowrap">
                  {fmtCosto(calc.rentabilidadNeta?.monto)} ({fmtPorcentaje(calc.rentabilidadNeta?.pctSobreCosto)})
                </span>
              </div>
              {calc.avisos.length > 0 && (
                <ul className="space-y-1 px-4 py-3 text-xs text-amber-700">
                  {calc.avisos.map(a => <li key={a.codigo}>⚠ {a.mensaje}</li>)}
                </ul>
              )}
            </div>
          ) : calc ? (
            <ul className="space-y-1 px-4 py-4 text-sm text-amber-700">
              {calc.avisos.map(a => <li key={a.codigo}>⚠ {a.mensaje}</li>)}
            </ul>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-slate-400">Ingresá el costo del proveedor</div>
          )}
        </div>
        {calc && precio != null && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500" title={AYUDA_MAYORISTAS}>Precios mayoristas</h2>
            <PreciosMayoristas c={calc} />
          </div>
        )}
      </div>
    </div>
  );
}
